import React, { useEffect, useRef, useState } from "react";
import { Card, Col, Flex, message, Switch, Table, Typography } from "antd";

import PageTitle from "../../components/PageTitle";
import IntlMessage from "../../components/IntlMessage";
import ActionBar from "../../components/ActionBar";
import _, { debounce } from "lodash";
import FilterBar from "../../components/FilterBar";
import dayjs from "dayjs";
import OpPaymentMethodForm from "./OpPaymentMethodForm";
import opPaymentMethodsApi from "../../api/op-payment-method";
import operatorsApi from "../../api/operators";
import paymentMethodApi from "../../api/paymentMethod";
import { field_payment_method } from "../../utils/constant";
import useTime from "../../hooks/useTime";

const { Text, Link } = Typography;

const OpPaymentMethod = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [opPaymentMethod, setOpPaymentMethod] = useState([]);
  const [currentData, setCurrentData] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [operator, setOperator] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState([]);
  const [total, setTotal] = useState(1);
  const [condition, setCondition] = useState({});
  const [refresh, setRefresh] = useState(false);
  const [filter, setFilter] = useState({
    page: 1,
    limit: 10,
    order_by: "id",
    order_dir: "DESC",
    keyword: "",
  });

  const { convertToAnyTimezone } = useTime();

  const isMount = useRef(false);
  const showModal = () => {
    setIsModalOpen(true);
  };

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const onModalOpen = (record) => {
    setCurrentData(record);
    setIsModalOpen(true);
  };

  const onModalClose = (record) => {
    setCurrentData("");
    setIsModalOpen(false);
  };

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        const payment = await opPaymentMethodsApi.getAll(filter);
        setOpPaymentMethod(payment.data);
        setTotal(payment.total);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [refresh, filter]);

  useEffect(() => {
    isMount.current = true;
    async function fetchData() {
      if (isMount) {
        var filter = {
          page: 1,
          limit: 9999,
          keyword: "",
          order_by: "id",
          order_dir: "ASC",
        };
        let operator = await operatorsApi.getAll(filter);
        setOperator(operator.data);
        let payment_method = await paymentMethodApi.getAll(filter);
        setPaymentMethod(payment_method.data);
      }
    }
    fetchData();
    return () => {
      isMount.current = false;
    };
  }, []);

  useEffect(() => {
    if (filter?.filter_name && filter?.filter_value) {
      const newCondition = _.zipObjectDeep(
        filter?.filter_name,
        filter?.filter_value
      );
      setCondition(newCondition);
    }
  }, [filter]);

  const columns = [
    {
      title: <IntlMessage id="global.payment_method" />,
      key: "payment_method.method",
      dataIndex: "payment_method_id",
      sorter: true,
      width: 250,
      render: (text, record, index) => {
        return (
          <Link
            onClick={() => onModalOpen(record)}
            style={{ fontSize: "small" }}
          >
            {record.payment_method?.display}
          </Link>
        );
      },
    },
    {
      title: <IntlMessage id="global.operators" />,
      key: "operator.shortname",
      sorter: true,
      width: 250,
      dataIndex: "operator_id",
      render: (text, record, index) => {
        return record.operator ? record.operator.title : null;
      },
    },
    {
      title: <IntlMessage id="global.merchant_account" />,
      key: "merchant_account",
      sorter: true,
      width: 250,
      dataIndex: "field",
      render: (text, record, index) => {
        const data = JSON.parse(record.field);
        if (record && _.isArray(JSON.parse(record.field))) {
          const merchant_account = field_payment_method.map((key) =>
            _.chain(JSON.parse(record.field))
              .keyBy("code")
              .mapValues("value")
              .value()
          );
          if (
            merchant_account[0] &&
            (merchant_account[0].merchant_account ||
              merchant_account[0].identifiant)
          ) {
            data.merchant_account =
              merchant_account[0].merchant_account ||
              merchant_account[0].identifiant;
          }
        }

        return (
          <>
            {data && (data.identifiant || data.merchant_account)
              ? data.identifiant || data.merchant_account
              : ""}
          </>
        );
      },
    },

    {
      title: <IntlMessage id="global.status" />,
      key: "status",
      width: 150,
      dataIndex: "status",
      render: (text, record, index) => {
        return (
          <Flex justify="center">
            <Switch
              defaultValue={record.status}
              checked={!!text}
              onChange={() => onChangeStatus(record, !text)}
            />
          </Flex>
        );
      },
    },

    {
      title: <IntlMessage id="global.created_at" />,
      key: "created_at",
      width: 250,
      dataIndex: "created_at",
      sorter: true,
      render: (text, record, index) => {
        return (
          <Text>
            {convertToAnyTimezone(record?.created_at).format(
              "DD/MM/YYYY HH:mm"
            )}
          </Text>
        );
      },
    },
  ];

  const getOrder = (order) => {
    if (order === "ascend") return "ASC";
    if (order === "descend") return "DESC";
    return "DESC";
  };

  const onChangeTable = (pagination, filters, sorter) => {
    const currentFilter = {
      ...filter,
      page: pagination.current,
      limit: pagination.pageSize,
    };

    if (sorter) {
      const { field, order } = sorter;
      if (field === "operator.short_name") {
        currentFilter.order_by = "operator";
      } else if (field === "payment_method.method") {
        currentFilter.order_by = "payment_method";
      } else {
        currentFilter.order_by = field || "id";
      }

      currentFilter.order_dir = getOrder(order);

      setFilter(currentFilter);
    }

    if (pagination.pageSize !== filter.page) {
      setOpPaymentMethod([]);
    }
  };

  const onDelete = async () => {
    await opPaymentMethodsApi.delete(selectedRowKeys).catch((err) => {
      console.log(err);
    });
    message.success("delete success");
    setSelectedRowKeys([]);
    onRefresh();
  };

  const onRefresh = () => {
    setRefresh((refresh) => !refresh);
  };

  const onCopy = async () => {
    if (selectedRowKeys.length > 0) {
      for (const key of selectedRowKeys) {
        await opPaymentMethodsApi.copy(key).catch((err) => {
          console.log(err);
        });
      }
      message.success("copy success");
      onRefresh();
    }
  };

  const onFilter = debounce((value, name, type) => {
    if (type === "search") {
      setFilter((filter) => {
        return { ...filter, keyword: value, page: 1 };
      });
    } else {
      const filter_obj = { ...condition, [name]: value };
      let filter_name = [];
      let filter_value = [];
      for (var key in filter_obj) {
        if (filter_obj[key]) {
          filter_name.push(key);
          filter_value.push(filter_obj[key]);
        }
      }
      setFilter((filter) => {
        return {
          ...filter,
          filter_name: filter_name,
          filter_value: filter_value,
        };
      });
    }
  }, 300);

  const onChangeStatus = async (record, checked) => {
    await opPaymentMethodsApi
      .updateStatus(record.id, { status: checked })
      .catch((err) => {
        console.log(err);
      });
    setOpPaymentMethod((data) => {
      return data.map((item) => {
        if (item.id === record.id) {
          return {
            ...item,
            status: checked,
          };
        }
        return item;
      });
    });
  };

  return (
    <React.Fragment>
      <Flex justify="space-between" align="center">
        <Col>
          <PageTitle title={<IntlMessage id="global.op_payment_method" />} />
        </Col>
        <Col>
          <ActionBar
            isShowAddButton={true}
            isShowCopyButton={true}
            onAdd={showModal}
            isShowDeleteButton={true}
            onCopy={onCopy}
            deleteMessage={
              "Warning! You are about to delete this payment method of operator and its associated records. Are you sure you want to delete this record?"
            }
            isShowPublishButtons={false}
            onDelete={onDelete}
            isDisabled={selectedRowKeys.length > 0 ? false : true}
            onRefresh={onRefresh}
          />
        </Col>
      </Flex>
      <Card bordered={false}>
        <Flex vertical gap={8}>
          <FilterBar
            onFilter={onFilter}
            data={[
              {
                name: "operator_id",
                data: operator,
                placeholder: <IntlMessage id="global.operator" />,
              },
              {
                name: "payment_method_id",
                data: paymentMethod,
                placeholder: <IntlMessage id="global.payment_method" />,
              },
              {
                name: "status",
                data: [
                  { title: "Active", value: "1" },
                  { title: "Inactive", value: "0" },
                ],
                placeholder: <IntlMessage id="global.status" />,
              },
            ]}
          />

          <Table
            columns={columns}
            tableLayout="auto"
            pagination={{
              limit: filter.limit,
              current: filter.page,
              total: total,
              showSizeChanger: true,
            }}
            size="small"
            onChange={onChangeTable}
            dataSource={opPaymentMethod}
            rowSelection={rowSelection}
            loading={loading}
            rowKey={"id"}
          />
        </Flex>
      </Card>
      {isModalOpen ? (
        <OpPaymentMethodForm
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          onModalClose={onModalClose}
          currentData={currentData}
          onRefresh={onRefresh}
        />
      ) : null}
    </React.Fragment>
  );
};

export default OpPaymentMethod;
